// Fallback text-walk extractor for MySQL stored-routine procedural locals
// (parameters + DECLAREd variables) — runs alongside the ANTLR-based
// `parseRoutineBody` path in `mysqlProceduralLocals.ts` to bypass a
// production-bundle-only ANTLR parse failure documented in
// `goals/autocomplete-coverage/diagnostics/MONACO_STATE_DIAG.md`.
//
// Symptoms in the bundled renderer: `parseRoutineBody` reports a syntax error
// around `DEFAULT 0` inside the procedure body and error-recovers past the
// remaining `DECLARE` statements; `scope.variables` returns only parameters.
// Vitest with the byte-identical parseSource succeeds. The fallback always
// runs and contributes the names ANTLR loses; `mysqlProceduralLocals.ts`
// merges + dedupes by name so the happy ANTLR path stays the source of truth
// for tier classification (innermost-local vs outer-local), while this layer
// guarantees DECLARED names surface even when ANTLR fails.
//
// Scope:
//   - Walks from buffer start (or last DELIMITER directive) up to the cursor
//   - Detects the most recent CREATE [DEFINER=…] PROCEDURE | FUNCTION |
//     TRIGGER | EVENT header before the cursor
//   - Extracts parameter names from the `(...)` between header and BEGIN
//   - Extracts DECLAREd variable names from the BEGIN…cursor region
//   - SKIPS `DECLARE … CURSOR FOR`, `DECLARE … CONDITION FOR`, and
//     `DECLARE … HANDLER FOR` (these are not assignable identifiers)
//
// Not in scope (left to the ANTLR path):
//   - Per-name shadowing (a DECLARE in an inner block that re-uses an outer
//     name). BEGIN/END block-close scoping IS modelled (F13): a DECLARE in a
//     nested/sibling block that has already closed before the cursor is no
//     longer surfaced — only still-open BEGIN frames contribute names.
//   - Data-type extraction (we surface a `detail` placeholder; the ANTLR
//     path provides the precise data type when available)
//   - User variables (`@name`) — handled in `emitMysqlProceduralLocalCandidates`

import { tokenize, type Token } from '../context/tokenizer';

export type InBufferProceduralName = {
  readonly name: string;
  readonly kind: 'parameter' | 'variable';
  /** For `kind === 'parameter'`, the mode keyword: 'in' / 'out' / 'inout'.
   *  Undefined when no mode keyword was seen before the name (MySQL treats
   *  the implicit default as IN). */
  readonly parameterMode?: 'in' | 'out' | 'inout';
};

/**
 * Detect the enclosing routine kind for the cursor — `'procedure'`,
 * `'function'`, `'trigger'`, `'event'`, or `null` when the cursor is not
 * inside any routine header's body region.
 *
 * Used to gate filters whose semantics differ across routine kinds. Example:
 * the IN-mode parameter filter at write-target slots applies only to
 * procedures, because MySQL function parameters have no mode keyword and the
 * autocomplete spec lists them as legitimate at write-target positions
 * (`SELECT … INTO p_film_id`).
 */
export function detectMysqlEnclosingRoutineKind(
  source: string,
  cursorOffset: number,
): 'procedure' | 'function' | 'trigger' | 'event' | null {
  const scanFrom = findLastDelimiterDirectiveOffset(source, cursorOffset);
  const tokens = tokenize(source, scanFrom, cursorOffset, 'mysql');
  const header = findLatestRoutineHeader(tokens);
  if (!header) return null;
  switch (header.kind) {
    case 'PROCEDURE': return 'procedure';
    case 'FUNCTION':  return 'function';
    case 'TRIGGER':   return 'trigger';
    case 'EVENT':     return 'event';
  }
}

/**
 * Walk the source text from the last DELIMITER directive to the cursor,
 * extracting names declared via `DECLARE <name> CURSOR FOR …`. Returns the
 * cursor-name identifiers visible at the cursor position — used by the
 * procedural-locals emitter to surface them at OPEN / FETCH / CLOSE slots.
 *
 * Cursor names live in a separate completion category from variables (they
 * are not assignable) — `findInBufferProceduralNames` deliberately drops
 * them. This helper recovers them when the slot calls for cursor identifiers.
 */
export function findInBufferCursorNames(
  source: string,
  cursorOffset: number,
): string[] {
  const scanFrom = findLastDelimiterDirectiveOffset(source, cursorOffset);
  const tokens = tokenize(source, scanFrom, cursorOffset, 'mysql');
  const header = findLatestRoutineHeader(tokens);
  if (!header) return [];
  const beginIdx = findBeginAfter(tokens, header.afterNameIdx);
  if (beginIdx === -1) return [];
  return extractDeclaredCursorNames(tokens, beginIdx + 1);
}

function extractDeclaredCursorNames(
  tokens: readonly Token[],
  fromIdx: number,
): string[] {
  const names: string[] = [];
  let i = fromIdx;
  while (i < tokens.length) {
    if (identUpper(tokens[i]) !== 'DECLARE') {
      i += 1;
      continue;
    }
    // After DECLARE: first identifier is the name candidate; the next
    // keyword tells us whether this is a CURSOR / CONDITION / HANDLER / a
    // variable declaration. Only the CURSOR shape interests us here.
    let j = i + 1;
    if (!(tokens[j]?.type === 'ident' || tokens[j]?.type === 'kw')) {
      i = findSemicolonAfter(tokens, j);
      if (i === -1) break;
      i += 1;
      continue;
    }
    const candidateName = unquoteIdent(tokens[j]!.text);
    j += 1;
    const afterUpper = identUpper(tokens[j]);
    if (afterUpper === 'CURSOR') {
      names.push(candidateName);
    }
    i = findSemicolonAfter(tokens, j);
    if (i === -1) break;
    i += 1;
  }
  return names;
}

export function findInBufferProceduralNames(
  source: string,
  cursorOffset: number,
): InBufferProceduralName[] {
  // Start from the last DELIMITER directive at-or-before the cursor — the
  // tokenizer doesn't recognise `DELIMITER //`, but everything inside the
  // current routine sits AFTER the most recent directive. Stripping the
  // pre-directive prefix avoids picking up routines from earlier batches.
  const scanFrom = findLastDelimiterDirectiveOffset(source, cursorOffset);
  const tokens = tokenize(source, scanFrom, cursorOffset, 'mysql');

  // Find the latest CREATE [DEFINER=…] PROCEDURE|FUNCTION|TRIGGER|EVENT
  // header BEFORE the cursor. If none, no routine context; bail.
  const header = findLatestRoutineHeader(tokens);
  if (!header) return [];

  const out: InBufferProceduralName[] = [];
  // 1. Parameters from the `(...)` immediately after the routine name.
  for (const p of extractParameterNames(tokens, header.afterNameIdx)) {
    out.push({ name: p.name, kind: 'parameter', parameterMode: p.mode });
  }
  // 2. DECLAREd variables between BEGIN and the cursor.
  const beginIdx = findBeginAfter(tokens, header.afterNameIdx);
  if (beginIdx === -1) return out;
  for (const name of extractDeclaredVariableNames(tokens, beginIdx + 1)) {
    out.push({ name, kind: 'variable' });
  }
  return out;
}

// ─── DELIMITER stripping ────────────────────────────────────────────────────

function findLastDelimiterDirectiveOffset(
  source: string,
  cursorOffset: number,
): number {
  // Match `DELIMITER <something>` line directives. The tokenizer treats them
  // as identifiers anyway; we just need to skip past the directive line so
  // the subsequent routine token sequence is clean. Search bounded to the
  // cursor.
  const slice = source.slice(0, cursorOffset);
  const re = /(^|\n)[ \t]*DELIMITER\b[^\n]*/gi;
  let lastEnd = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(slice)) !== null) {
    lastEnd = m.index + m[0].length;
  }
  return lastEnd;
}

// ─── Routine header detection ───────────────────────────────────────────────

type RoutineHeader = {
  readonly kind: 'PROCEDURE' | 'FUNCTION' | 'TRIGGER' | 'EVENT';
  /** Token index of the routine-kind keyword. */
  readonly kindIdx: number;
  /** Token index AFTER the routine name (qualified or bare). */
  readonly afterNameIdx: number;
};

function findLatestRoutineHeader(
  tokens: readonly Token[],
): RoutineHeader | null {
  let latest: RoutineHeader | null = null;
  let i = 0;
  while (i < tokens.length) {
    const tok = tokens[i]!;
    if (identUpper(tok) !== 'CREATE' && identUpper(tok) !== 'ALTER') {
      i += 1;
      continue;
    }
    // Optional DEFINER=user clause — skip 3+ tokens until next keyword.
    let j = skipDefiner(tokens, i + 1);
    const kindTok = tokens[j];
    const kindUpper = identUpper(kindTok);
    if (
      kindUpper !== 'PROCEDURE' &&
      kindUpper !== 'FUNCTION' &&
      kindUpper !== 'TRIGGER' &&
      kindUpper !== 'EVENT'
    ) {
      i += 1;
      continue;
    }
    const kind = kindUpper as RoutineHeader['kind'];
    const kindIdx = j;
    j += 1;
    // Optional `IF NOT EXISTS`.
    if (
      identUpper(tokens[j]) === 'IF' &&
      identUpper(tokens[j + 1]) === 'NOT' &&
      identUpper(tokens[j + 2]) === 'EXISTS'
    ) {
      j += 3;
    }
    // Routine name (possibly qualified `db.name`).
    if (!isIdentLike(tokens[j])) {
      i += 1;
      continue;
    }
    j += 1;
    if (tokens[j]?.type === 'dot' && isIdentLike(tokens[j + 1])) {
      j += 2;
    }
    latest = { kind, kindIdx, afterNameIdx: j };
    i = j;
  }
  return latest;
}

function skipDefiner(tokens: readonly Token[], startIdx: number): number {
  if (identUpper(tokens[startIdx]) !== 'DEFINER') return startIdx;
  // DEFINER = value — advance until we hit a routine-kind keyword.
  let j = startIdx + 1;
  while (j < tokens.length) {
    const u = identUpper(tokens[j]);
    if (u === 'PROCEDURE' || u === 'FUNCTION' || u === 'TRIGGER' || u === 'EVENT') {
      return j;
    }
    j += 1;
    if (j - startIdx > 16) break;
  }
  return startIdx;
}

// ─── Parameter extraction ───────────────────────────────────────────────────

type ParsedParameter = {
  readonly name: string;
  readonly mode?: 'in' | 'out' | 'inout';
};

function extractParameterNames(
  tokens: readonly Token[],
  fromIdx: number,
): ParsedParameter[] {
  // Find the `(` opening the parameter list.
  let i = fromIdx;
  while (i < tokens.length && tokens[i]!.type !== 'lparen') i += 1;
  if (i === tokens.length) return [];
  const lparenIdx = i;
  const rparenIdx = findMatchingRParen(tokens, lparenIdx);
  if (rparenIdx === -1) return [];

  // Walk the comma-separated parameter list at depth 0.
  const params: ParsedParameter[] = [];
  let segStart = lparenIdx + 1;
  let depth = 0;
  for (let k = lparenIdx + 1; k < rparenIdx; k += 1) {
    const t = tokens[k]!;
    if (t.type === 'lparen') depth += 1;
    else if (t.type === 'rparen') depth -= 1;
    else if (t.type === 'comma' && depth === 0) {
      const p = parseParameter(tokens, segStart, k);
      if (p) params.push(p);
      segStart = k + 1;
    }
  }
  if (segStart < rparenIdx) {
    const p = parseParameter(tokens, segStart, rparenIdx);
    if (p) params.push(p);
  }
  return params;
}

function parseParameter(
  tokens: readonly Token[],
  start: number,
  end: number,
): ParsedParameter | null {
  // Each parameter shape: `[IN|OUT|INOUT] name datatype`. The first non-
  // mode-keyword identifier/keyword is the name. The mode keyword (when
  // present) is captured separately so callers can apply mode-aware filters
  // (e.g. drop IN-mode params from assignment-target slots).
  let i = start;
  let mode: ParsedParameter['mode'];
  while (i < end) {
    const u = identUpper(tokens[i]);
    if (u === 'IN') {
      mode = 'in';
      i += 1;
      continue;
    }
    if (u === 'OUT') {
      mode = 'out';
      i += 1;
      continue;
    }
    if (u === 'INOUT') {
      mode = 'inout';
      i += 1;
      continue;
    }
    if (isIdentLike(tokens[i])) {
      return { name: unquoteIdent(tokens[i]!.text), mode };
    }
    i += 1;
  }
  return null;
}

// ─── DECLARE variable extraction ────────────────────────────────────────────

function findBeginAfter(tokens: readonly Token[], fromIdx: number): number {
  for (let i = fromIdx; i < tokens.length; i += 1) {
    if (identUpper(tokens[i]) === 'BEGIN') return i;
  }
  return -1;
}

function extractDeclaredVariableNames(
  tokens: readonly Token[],
  fromIdx: number,
): string[] {
  // F13: block-close awareness. `fromIdx` points just past the routine's
  // outer BEGIN, so we start one block deep. We keep a stack of name lists —
  // one frame per open BEGIN … END block. A nested/sibling block that closes
  // before the cursor pops its frame, dropping its DECLAREs from scope. Only
  // names in still-open frames at the cursor (= end of the bounded token
  // stream) are returned. Compound blocks (IF/LOOP/WHILE/REPEAT/CASE, closed
  // by `END IF`/`END LOOP`/…) never hold DECLAREs and must NOT pop a BEGIN
  // frame, so we skip the modifier keyword after such an `END`.
  //
  // (Pragmatic scope: BEGIN/END depth only — full per-name shadowing across
  // re-declared identifiers in sibling blocks is not modelled.)
  const frames: string[][] = [[]];
  let i = fromIdx;
  while (i < tokens.length) {
    const u = identUpper(tokens[i]);
    if (u === 'BEGIN') {
      // Open a new block scope.
      frames.push([]);
      i += 1;
      continue;
    }
    if (u === 'END') {
      const nextUpper = identUpper(tokens[i + 1]);
      const isCompoundClose =
        nextUpper === 'IF' ||
        nextUpper === 'LOOP' ||
        nextUpper === 'WHILE' ||
        nextUpper === 'REPEAT' ||
        nextUpper === 'CASE';
      if (isCompoundClose) {
        // `END IF` / `END LOOP` / … closes a compound block, not a BEGIN
        // scope. Consume the modifier and leave the frame stack untouched.
        i += 2;
        continue;
      }
      // Bare `END` closes the innermost BEGIN block — pop its frame so its
      // DECLAREs go out of scope. Never pop the implicit base frame.
      if (frames.length > 1) frames.pop();
      i += 1;
      continue;
    }
    if (u !== 'DECLARE') {
      i += 1;
      continue;
    }
    // After DECLARE: collect alternating <ident/kw> <,> tokens until we hit
    // a token that breaks the pattern. The terminator is the data-type's
    // first token (ident/kw immediately following an ident with no comma),
    // a CURSOR / CONDITION / HANDLER keyword (non-variable DECLARE shape),
    // or any non-identifier non-comma token.
    let j = i + 1;
    const headNames: string[] = [];
    let expectingName = true;
    while (j < tokens.length) {
      const t = tokens[j]!;
      if (expectingName) {
        if (t.type !== 'ident' && t.type !== 'kw') break;
        headNames.push(unquoteIdent(t.text));
        expectingName = false;
        j += 1;
        continue;
      }
      // Expecting either a comma (more names) or the start of the data-type
      // / non-variable-DECLARE marker.
      if (t.type === 'comma') {
        expectingName = true;
        j += 1;
        continue;
      }
      break;
    }
    // If the loop broke because the next token is CURSOR / CONDITION /
    // HANDLER, this is a `DECLARE <ident> CURSOR FOR …`, `DECLARE <ident>
    // CONDITION FOR …`, `DECLARE [EXIT|CONTINUE|UNDO] HANDLER FOR …` — none
    // of these introduce assignable variable names. Discard the collected
    // names rather than treating them as locals.
    const afterUpper = identUpper(tokens[j]);
    if (
      afterUpper === 'CURSOR' ||
      afterUpper === 'CONDITION' ||
      afterUpper === 'HANDLER'
    ) {
      headNames.length = 0;
    }
    // Collect into the CURRENT (innermost still-open) block frame, so that
    // when that block later closes before the cursor these names are popped
    // out of scope.
    const current = frames[frames.length - 1]!;
    for (const n of headNames) current.push(n);
    // Skip past the DECLARE statement to find its terminating `;`.
    i = findSemicolonAfter(tokens, j);
    if (i === -1) break;
    i += 1;
  }
  // Flatten the still-open frames (innermost last) — these are exactly the
  // DECLAREs whose enclosing BEGIN block has not closed before the cursor.
  return frames.flat();
}

// ─── Shared token helpers ───────────────────────────────────────────────────

function findMatchingRParen(tokens: readonly Token[], lparenIdx: number): number {
  let depth = 0;
  for (let k = lparenIdx; k < tokens.length; k += 1) {
    const t = tokens[k]!;
    if (t.type === 'lparen') depth += 1;
    else if (t.type === 'rparen') {
      depth -= 1;
      if (depth === 0) return k;
    }
  }
  return -1;
}

function findSemicolonAfter(tokens: readonly Token[], fromIdx: number): number {
  for (let k = fromIdx; k < tokens.length; k += 1) {
    if (tokens[k]!.type === 'semicolon') return k;
  }
  return -1;
}

function isIdentLike(t: Token | undefined): boolean {
  if (!t) return false;
  return t.type === 'ident' || t.type === 'kw';
}

function unquoteIdent(text: string): string {
  if (text.length >= 2 && text[0] === '`' && text[text.length - 1] === '`') {
    return text.slice(1, -1).replace(/``/g, '`');
  }
  if (text.length >= 2 && text[0] === '"' && text[text.length - 1] === '"') {
    return text.slice(1, -1).replace(/""/g, '"');
  }
  return text;
}

function identUpper(t: Token | undefined): string | null {
  if (!t) return null;
  if (t.type !== 'ident' && t.type !== 'kw') return null;
  return unquoteIdent(t.text).toUpperCase();
}
